import { ProviderDataType } from "@/api/ProviderApi";
import { normalizeIdentification } from "@utils/Identification";
import { ColumnMapping, mapRowToProvider } from "./providerImportMapping";
import { nextAvailableCode as nextCodeFrom, resolveRows } from "./core/resolveRows";
import { DuplicatePolicy, ResolveResult as GenericResolveResult, ResolvedRow as GenericResolvedRow, RowStatus } from "./core/types";

export type { DuplicatePolicy, RowStatus };

export type ResolvedRow = GenericResolvedRow<ProviderDataType>;

export type ResolveResult = GenericResolveResult<ProviderDataType>;

/** Siguiente codigo consecutivo disponible entre los registros ya existentes. */
export const nextAvailableCode = (items: Array<{ codigo?: unknown }>): number =>
    nextCodeFrom(items.map((item) => item.codigo as string | number | null | undefined));

/**
 * Transforma las filas del archivo en proveedores listos para importar: normaliza los
 * valores, detecta duplicados contra lo que ya existe en el tenant y asigna los codigos
 * que falten.
 *
 * No hay entidades relacionadas que resolver, asi que el resultado es el del motor
 * generico tal cual.
 */
export const resolveProviderRows = (params: {
    dataRows: string[][];
    mapping: ColumnMapping;
    existingProviders: ProviderDataType[];
    policy: DuplicatePolicy;
}): ResolveResult => {
    const { dataRows, mapping, existingProviders, policy } = params;

    return resolveRows<ProviderDataType, ProviderDataType>({
        dataRows,
        mapping,
        policy,
        existing: existingProviders,
        entitySingular: "proveedor",

        mapRow: (row, columnMapping) => {
            const { provider, lookupName } = mapRowToProvider(row, columnMapping);
            return { data: provider, lookupName };
        },

        prepare: (provider) => {
            const messages: string[] = [];

            if (!`${provider.nombre ?? ""}`.trim()) {
                return { error: "La fila no tiene nombre de proveedor", messages };
            }

            // `identificacion` es sort key del GSI listProviderByIdentificacionTenant:
            // una cadena vacia rompe el PutItem, asi que se quita del registro.
            const identificacion = normalizeIdentification(`${provider.identificacion ?? ""}`);
            if (!identificacion) {
                delete provider.identificacion;
                messages.push(
                    "Sin identificacion: no podra vincularse automaticamente con facturas electronicas recibidas"
                );
            }

            return { error: null, messages };
        },

        duplicateKeys: (provider) => [
            codeKey(provider.codigo),
            identificationKey(provider.identificacion),
        ],
        existingKeys: (provider) => [codeKey(provider.codigo), identificationKey(provider.identificacion)],
        idOf: (provider) => provider.id,

        codeOf: (provider) => `${provider.codigo ?? ""}`,
        setCode: (provider, code) => {
            provider.codigo = code;
        },
        existingCodeOf: (provider) => `${provider.codigo ?? ""}`,

        // Al importar un duplicado como registro nuevo hay que soltar lo que identifica al
        // original: el codigo lo reasigna el motor, y la identificacion no puede repetirse
        // o el proveedor nuevo secuestraria el enlace de las facturas electronicas.
        onDuplicateAsNew: (provider, messages) => {
            if (`${provider.identificacion ?? ""}`.trim()) {
                delete provider.identificacion;
                delete provider.tipo_identificacion;
                messages.push("Se importa sin identificacion para no repetir la del proveedor existente");
            }
        },
    });
};

const codeKey = (codigo?: unknown): string => {
    const value = `${codigo ?? ""}`.trim();
    return value ? `codigo:${value}` : "";
};

const identificationKey = (identificacion?: unknown): string => {
    const value = normalizeIdentification(`${identificacion ?? ""}`);
    return value ? `identificacion:${value}` : "";
};
